import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Descriptions, Form, Table, Tag, Typography, Space } from "antd";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import { Link, useLocation, useParams } from "react-router-dom";
import { useMercuryContext } from "context";
import { ROUTES } from "common/constants/routes";
import { checkPermissions } from "common/utils/checkPermissions";
import { getUserDashboardSelection, resolveUserRoleObjects } from "common/utils/userDashboard";
import { failedNotification } from "common/utils/notifications";
import TimezoneConverter from "components/timezone-converter";
import { fetchLanguageCodes } from "filters/LanguageCode.filter";
import { ExpandableTagList } from "shared/components/ExpandableTagList";
import EditUser from "./EditUser";
import {
  buildUserFormValues,
  normalizeUserEditId,
  runWithOptionalLoading,
} from "./userEditUtils";
import { saveUserEdits } from "./userEditService";
import {
  buildUserAccessRows,
  getUserDetailsItemName,
  UserDetailsCustomerApplication,
  UserDetailsDashboard,
  UserDetailsRole,
  UserDetailsUser,
} from "./userDetailsAccessRows";
import "./manage-users.scss";

const { Text } = Typography;

interface UserDetailsPageUser extends UserDetailsUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  targetLanguages?: Array<string | number | { value?: string; name?: string; title?: string; label?: string }>;
  admin?: boolean;
  vendorManager?: boolean;
  isActive?: boolean;
  lastLogin?: string;
}

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
    };
  };
}

const parseDisplayValues = (
  items: UserDetailsPageUser["targetLanguages"] | undefined,
  key: string,
  namesById: Record<string, string> = {}
) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => getUserDetailsItemName(item, key, namesById, { allowPrimitiveDisplay: true }))
    .filter(Boolean) as string[];
};

const formatUserName = (user: UserDetailsPageUser | null) => {
  if (!user) return "User";
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : user.email || "User";
};

const getLastLoginValue = (user: UserDetailsPageUser): string | null => {
  const isMeaningfulValue = (value: unknown) =>
    value !== null && value !== undefined && value !== "" && value !== "null";

  const findLastLoginValue = (value: unknown): string | null => {
    if (!value || typeof value !== "object") {
      return null;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = findLastLoginValue(item);
        if (found) return found;
      }
      return null;
    }

    for (const [key, nestedValue] of Object.entries(value as Record<string, unknown>)) {
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
      const matchesLoginField =
        normalizedKey.includes("lastlogin") ||
        normalizedKey.includes("logintime") ||
        normalizedKey.includes("loginat") ||
        normalizedKey.includes("logindate") ||
        normalizedKey.includes("logintimestamp") ||
        normalizedKey.includes("loginutc") ||
        normalizedKey.includes("lastseen") ||
        normalizedKey.includes("loggedinat");

      if (matchesLoginField) {
        const candidate = nestedValue;
        if (isMeaningfulValue(candidate)) {
          if (typeof candidate !== "object") return String(candidate);

          for (const wrapperKey of ["$date", "date", "value", "timestamp"]) {
            const wrappedValue = (candidate as Record<string, unknown>)[wrapperKey];
            if (isMeaningfulValue(wrappedValue) && typeof wrappedValue !== "object") {
              return String(wrappedValue);
            }
          }

          const found = findLastLoginValue(candidate);
          if (found) return found;
        }
      }

      const found = findLastLoginValue(nestedValue);
      if (found) return found;
    }

    return null;
  };

  return findLastLoginValue(user);
};

const UserDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const context = useMercuryContext();
  const [user, setUser] = useState<UserDetailsPageUser | null>(null);
  const [applicationsList, setApplicationsList] = useState<any[]>([]);
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [dashboardsList, setDashboardsList] = useState<any[]>([]);
  const [permissionsList, setPermissionsList] = useState<any[]>([]);
  const [languageCodes, setLanguageCodes] = useState<{ title: string; value: string }[]>([]);
  const [applicationNames, setApplicationNames] = useState<Record<string, string>>({});
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});
  const [roleDetailsById, setRoleDetailsById] = useState<Record<string, UserDetailsRole>>({});
  const [dashboardNames, setDashboardNames] = useState<Record<string, string>>({});
  const [dashboardDetailsById, setDashboardDetailsById] = useState<Record<string, UserDetailsDashboard>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleEditDrawer, setVisibleEditDrawer] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [editingForm] = Form.useForm();
  const editFormRef = useRef<any>(null);

  const userPermissions = context.userDetails?.permissions || [];
  const canEditUser = checkPermissions(userPermissions, [
    { customerApplication: "Mercury", permissionName: "Users", action: "edit" },
  ]);

  const fetchUser = useCallback(async (showLoading = true) => {
    if (!id) {
      setError("User ID is missing.");
      setLoading(false);
      return;
    }

    await runWithOptionalLoading(showLoading, setLoading, async () => {
      try {
        setError(null);
        const { data } = await context.umsClient.get(
          `/ums-v1/users/${encodeURIComponent(id)}`
        );
        setUser({
          ...data,
          lastLogin: data?.lastLogin ?? (location.state as { lastLogin?: string } | null)?.lastLogin,
        });

        const [applicationsResult, rolesResult, dashboardsResult] = await Promise.allSettled([
          context.coreClient.get("core-v1/customerApplications/paginate?page=1&limit=500"),
          context.umsClient.get("/ums-v1/roles"),
          context.umsClient.get("/ums-v1/dashboards"),
        ]);

        if (applicationsResult.status === "fulfilled") {
          const applications: UserDetailsCustomerApplication[] = applicationsResult.value?.data?.[0]?.documents || [];
          setApplicationsList(applications);
          setApplicationNames(
            applications.reduce((names: Record<string, string>, application) => {
              const applicationId = application._id || application.id;
              if (applicationId && application.name) names[String(applicationId)] = application.name;
              return names;
            }, {})
          );
        }

        if (rolesResult.status === "fulfilled") {
          const roles: UserDetailsRole[] = Array.isArray(rolesResult.value?.data) ? rolesResult.value.data : [];
          setRolesList(roles);
          setRoleNames(
            roles.reduce((names: Record<string, string>, role) => {
              const roleId = role._id || role.id;
              if (roleId && role.name) names[String(roleId)] = role.name;
              return names;
            }, {})
          );
          setRoleDetailsById(
            roles.reduce((details: Record<string, UserDetailsRole>, role) => {
              const roleId = role._id || role.id;
              if (roleId) details[String(roleId)] = role;
              return details;
            }, {})
          );
        }

        if (dashboardsResult.status === "fulfilled") {
          const dashboards: UserDetailsDashboard[] = Array.isArray(dashboardsResult.value?.data)
            ? dashboardsResult.value.data
            : [];
          setDashboardsList(dashboards);
          setDashboardNames(
            dashboards.reduce((names: Record<string, string>, dashboard) => {
              const dashboardId = dashboard._id || dashboard.id;
              if (dashboardId && dashboard.name) names[String(dashboardId)] = dashboard.name;
              return names;
            }, {})
          );
          setDashboardDetailsById(
            dashboards.reduce((details: Record<string, UserDetailsDashboard>, dashboard) => {
              const dashboardId = dashboard._id || dashboard.id;
              if (dashboardId) details[String(dashboardId)] = dashboard;
              return details;
            }, {})
          );
        }
      } catch (fetchError: unknown) {
        setError((fetchError as ApiErrorResponse)?.response?.data?.message || "Unable to load user details.");
      }
    });
  }, [context.coreClient, context.umsClient, id, location.state]);

  useEffect(() => {
    void fetchUser();
  }, [fetchUser]);

  const targetLanguages = useMemo(
    () => parseDisplayValues(user?.targetLanguages, "value"),
    [user?.targetLanguages]
  );

  // Group dashboards and roles under the customer application that owns each item.
  const accessRows = useMemo(() => {
    return buildUserAccessRows({
      user,
      applicationNames,
      dashboardDetailsById,
      dashboardNames,
      roleDetailsById,
      roleNames,
    });
  }, [applicationNames, dashboardDetailsById, dashboardNames, roleDetailsById, roleNames, user]);

  const accessColumns = [
    {
      title: "Customer Applications",
      dataIndex: "customerApplication",
      render: (customerApplication: string) => (
        <ExpandableTagList items={[customerApplication]} limit={1} tagColor="purple" />
      ),
    },
    {
      title: "Dashboards",
      dataIndex: "dashboards",
      render: (items: string[]) => (
        <ExpandableTagList items={items} emptyPlaceholder="-" limit={10} tagColor="blue" />
      ),
    },
    {
      title: "Roles",
      dataIndex: "roles",
      render: (items: string[]) => (
        <ExpandableTagList items={items} emptyPlaceholder="-" limit={10} tagColor="green" />
      ),
    },
  ];

  const handleEditUser = async () => {
    if (!id || !user) return;

    try {
      const [permissionsResult, languageCodesResult] = await Promise.all([
        permissionsList.length
          ? Promise.resolve(permissionsList)
          : context.umsClient.get("ums-v1/permissions").then((response: any) => response.data),
        languageCodes.length ? Promise.resolve(languageCodes) : fetchLanguageCodes(context),
      ]);
      const normalizedPermissions = permissionsResult.map((permission: any) => ({
        ...permission,
        key: permission.id,
      }));
      const roleObjects = resolveUserRoleObjects((user as any).roles || [], rolesList);
      const customerApplications = ((user as any).customerApplications || []).map((application: any) => {
        const applicationId = normalizeUserEditId(application);
        return applicationsList.find(
          (candidate) => normalizeUserEditId(candidate) === applicationId
        ) || application;
      });
      const row = { ...user, roles: roleObjects, customerApplications } as any;
      const { originalUserDashboardIds, dashboardIds } = getUserDashboardSelection({
        row,
        rolesList,
        dashboardsList,
      });

      row.dashboards = dashboardIds;
      row.__originalUserDashboards = originalUserDashboardIds;
      editingForm.resetFields();
      editingForm.setFieldsValue(buildUserFormValues({
        user: row,
        rolesList,
        permissionsList: normalizedPermissions,
        dashboardIds,
      }));
      setPermissionsList(normalizedPermissions);
      setLanguageCodes(languageCodesResult);
      setEditing({ editingRow: row, userLevelPermissions: row.userLevelPermissions || [] });
      setVisibleEditDrawer(true);
    } catch (editError: any) {
      failedNotification(
        "Unable to edit user",
        editError?.response?.data?.message || "Unable to load the Edit User form."
      );
    }
  };

  const closeEditDrawer = () => {
    setVisibleEditDrawer(false);
    setEditing(null);
    editingForm.resetFields();
  };

  const handleFinishEditing = async (values: Record<string, any>) => {
    if (!id || !editing?.editingRow) return false;

    return saveUserEdits({
      umsClient: context.umsClient,
      socket: context.socket,
      userId: id,
      values,
      rolesList,
      originalUser: editing.editingRow,
      canManagePrivileges: context.isAdmin,
      // Refresh the details without replacing the page with its loading state.
      onSaved: () => fetchUser(false),
    });
  };

  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <Text>Loading user details...</Text>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div style={{ padding: "24px" }}>
        <Text type="danger">{error || "User not found."}</Text>
      </div>
    );
  }

  const fullName = formatUserName(user);
  const lastLoginValue = getLastLoginValue(user);

  return (
    <>
      <Helmet>
        <title>{fullName} - Mercury © RWS</title>
      </Helmet>
      <div style={{ padding: "24px 32px 32px" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Link to={ROUTES.MANAGE_USERS}>
              <Button icon={<ArrowLeftOutlined />}>Back to Manage Users</Button>
            </Link>
            {canEditUser && (
              <Button type="primary" icon={<EditOutlined />} onClick={handleEditUser}>
                Edit
              </Button>
            )}
          </Space>

          <Card className="user-details-card">
            <Descriptions
              layout="vertical"
              column={{ xs: 1, sm: 2, md: 3, lg: 4 }}
              bordered
              title={(
                <div className="user-details-card__title">
                  <div>{fullName}</div>
                  <div className="user-details-card__last-login">
                    <Text type="secondary">Last Login</Text>
                    <Text>
                      {lastLoginValue ? <TimezoneConverter date={lastLoginValue} /> : "-"}
                    </Text>
                  </div>
                </div>
              )}
            >
              <Descriptions.Item label="First Name">{user.firstName || "-"}</Descriptions.Item>
              <Descriptions.Item label="Last Name">{user.lastName || "-"}</Descriptions.Item>
              <Descriptions.Item label="Email Address">{user.email || "-"}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card>
            <Descriptions
              className="user-details-languages"
              layout="horizontal"
              column={1}
              bordered
              title="Languages"
            >
              <Descriptions.Item label="Target Languages">
                <ExpandableTagList items={targetLanguages} emptyPlaceholder="-" maxLines={2} />
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card>
            <div className="user-details-flags">
              <Text className="user-details-flags__label">Flags</Text>
              <Descriptions
                className="user-details-flags__values"
                layout="vertical"
                column={{ xs: 1, sm: 3 }}
                bordered
              >
                <Descriptions.Item label="Admin">
                  {user.admin ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Vendor Manager">
                  {user.vendorManager ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Active">
                  {user.isActive ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Card>

          <Card>
            <Text className="user-details-access__title">Access and Configuration</Text>
            <Table
              className="user-details-access__table"
              columns={accessColumns}
              dataSource={accessRows}
              pagination={false}
              rowKey="key"
              size="small"
            />
          </Card>
        </Space>
      </div>
      {editing && (
        <EditUser
          handlers={{ closeEditDrawer, onFinishEditing: handleFinishEditing }}
          values={{
            visibleEditDrawer,
            editingForm,
            applicationsList,
            rolesList,
            permissionsList,
            dashboardsList,
            editing,
            editFormRef,
            languageCodes,
            canManagePrivileges: context.isAdmin,
          }}
        />
      )}
    </>
  );
};

export default UserDetailsPage;
